/***********************************************************************
 An instance of this class handles multiple sco's and act to the LMS
 as a single SCO with the given Scorm 1.2 API.
 The stored data will be saved compresses and encrypted in the
 suspend data field on LMS.
 ***********************************************************************/
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Assert } from "@/common/Assert";
import { Event } from "@/common/Event";
import { LoggingService as logger } from "@/logging/LoggingService";
import { SequenceDataMap } from "@/course/SequenceDataMap";
import { CourseEvents } from "@/course/CourseEvents";
import { Sco } from "@/scorm/common/Sco";
import { createCourseLessons } from "./createCourseLessons";
import { getCmi5 } from "./cmi5Api/cmi5";
import { CourseStatusStatement } from "./statements/CourseStatusStatement";
import { SuspendDataStatement } from "./statements/SuspendDataStatement";
import { createCourseData } from "@/cmi5/createCourseData";
import { sendCourseStatements } from "@/cmi5/statements/sendCourseStatements";
import { Cmi5SessionTerminator } from "@/cmi5/Cmi5SessionTerminator";
import { calculateCourseState } from "@/cmi5/status/calculateCourseState";
import { updateCourseState } from "@/cmi5/status/updateCourseState";
import { updateSuspendData } from "@/cmi5/status/updateSuspendData";
import { sendSuspendDataStatement } from "@/cmi5/statements/sendSuspendDataStatement";
export function createCourse(defaultMasteryScore = 80) {
    Assert.checkArgumentType(defaultMasteryScore, "number");
    const cmi5 = getCmi5();
    const lessons = createCourseLessons();
    // @ts-ignore
    const domStructure = new SequenceDataMap();
    let courseData, activeLesson = undefined, username = "", masteryScore = defaultMasteryScore, suspendData = "", courseState = {
        completed: false,
        failed: false,
        passed: false,
        score: 0
    };
    function onAfterInitializeStatement(error) {
        return __awaiter(this, void 0, void 0, function* () {
            if (error) {
                logger.error(`Error on cmi5 course initialization: ${error}`);
                throw error;
            }
            Cmi5SessionTerminator.initialize();
            logger.debug("Course successfully initialized!");
        });
    }
    function onAfterLoadLaunchData(error) {
        return __awaiter(this, void 0, void 0, function* () {
            if (error) {
                console.error(`Error while getting cmi5 launch data: ${error}`);
                return;
            }
            const cmi5MasteryScore = cmi5.getMasteryScore();
            masteryScore = cmi5MasteryScore ? (parseFloat(cmi5MasteryScore) * 100) : defaultMasteryScore;
            const actor = cmi5.getActor();
            username = actor.name;
            logger.debug("Read data...");
            courseState = yield CourseStatusStatement.get();
            suspendData = yield SuspendDataStatement.get();
            courseData = createCourseData(cmi5.getLaunchMode(), courseState, suspendData, masteryScore);
            lessons.initialize(courseData.getSuspendData());
            refreshUI();
        });
    }
    function commit() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!courseData) {
                return false;
            }
            const newCourseState = calculateCourseState(lessons.getStatusForResultRelevantScos(), masteryScore);
            const updatedCourseState = updateCourseState(courseState, newCourseState);
            if (updatedCourseState.changed.someValue) {
                courseState = updatedCourseState.value;
                courseData.updateCourseState(courseState);
                yield sendCourseStatements(updatedCourseState);
            }
            const newSuspendData = lessons.collectSuspendData();
            const updatedSuspendData = updateSuspendData(suspendData, newSuspendData);
            if (updatedSuspendData.changed) {
                suspendData = updatedSuspendData.value;
                courseData.updateSuspendData(suspendData);
                yield sendSuspendDataStatement(suspendData);
            }
            refreshUI();
            return true;
        });
    }
    function refreshUI() {
        Event.trigger(document, CourseEvents.BUFFER_REFRESHED);
    }
    function initializeActiveLearningSco(lessonId) {
        if (!lessonId || activeLesson) {
            return false;
        }
        if (!lessons.contains(lessonId)) {
            lessons.create(lessonId);
        }
        const scorm_1_3_data = lessons.getLessonData(lessonId);
        // @ts-ignore
        activeLesson = new Sco(lessonId, scorm_1_3_data);
        return true;
    }
    function terminateActiveLearningSco() {
        return __awaiter(this, void 0, void 0, function* () {
            logger.debug("Terminate active lesson...");
            if (!activeLesson) {
                logger.error("Terminate active lessen without active lesson!?");
                return false;
            }
            const changedData = activeLesson.getChangedData();
            for (const [scormModelId, value] of changedData.entries()) {
                lessons.setLessonValue(activeLesson.getId(), scormModelId, value);
            }
            Event.trigger(document, CourseEvents.SEQUENCE_TERMINATED, { scoId: activeLesson.getId() });
            activeLesson = undefined;
            return yield commit();
        });
    }
    // revealing public api
    return {
        domStructure: domStructure,
        username: username,
        /***********************************************************************
         * initializes the course by getting initial values from lms.
         ***********************************************************************/
        initialize: () => __awaiter(this, void 0, void 0, function* () {
            yield cmi5.start({
                initializeStatement: onAfterInitializeStatement,
                launchData: onAfterLoadLaunchData
            });
            return Promise.resolve();
        }),
        /***********************************************************************
         * gets the course mastery score
         * @return {Number} mastery score
         ***********************************************************************/
        getMasteryScore: () => masteryScore,
        /***********************************************************************
         * gets the completion threshold. What is the mastery score for scorm 1.2.
         * @return {Number} mastery score
         ***********************************************************************/
        getCompletionThreshold: () => masteryScore,
        /***********************************************************************
         * Calculates the current course score and status and commit all updated
         * values to the lms.
         * @return {String} true / false
         ***********************************************************************/
        commit,
        /***********************************************************************
         * sets the list for the scoring relevant scos
         * @param {boolean} useAssessments
         * @return {String} true
         ***********************************************************************/
        setScoreList: (useAssessments) => {
            domStructure.initScoreIDs(useAssessments);
            lessons.setRelevantScoList(domStructure.getScoreIDs());
            return "true";
        },
        /***********************************************************************
         * gets a value for the course
         * @param {String} modelId scorm data model id for the value
         * @return {String} value or an empty string, if the does not exist
         ***********************************************************************/
        getValue: (modelId) => {
            Assert.checkArgument(modelId);
            if (!courseData) {
                return undefined;
            }
            return courseData.get(modelId);
        },
        /***********************************************************************
         * sets a value for the course
         * @param {String} modelId scorm data model id for the the value
         * @param {String} value value
         * @return {String} true / false
         ***********************************************************************/
        setValue: (modelId, value) => {
            Assert.checkArgument(modelId);
            if (!courseData) {
                logger.debug("setValue without courseData!");
            }
            if (courseData.set(modelId, value)) {
                return true;
            }
            return false;
        },
        getLesson: (lessonId) => lessons.getLesson(lessonId),
        terminate: function () {
            return __awaiter(this, void 0, void 0, function* () {
                yield Cmi5SessionTerminator.terminate();
                return true;
            });
        },
        hasActiveLearningSco: () => !!activeLesson,
        getActiveLearningSco: () => activeLesson,
        initializeActiveLearningSco: initializeActiveLearningSco,
        terminateActiveLearningSco: terminateActiveLearningSco,
        onDomAndModelSynchronized: refreshUI
    };
}
